/**
 * スポット入札 CSV (通常 / ブロック) の雛形本文 (Step1「テンプレートを
 * ダウンロード」、common.md AC-DUC-031)。
 *
 * 通常入札・ブロック入札とも、 ヘッダー行 + spec §2「雛形の記入例データ」の行を出す
 * (通常入札 = `buildSpotNormalCsvTemplate`、 ブロック入札 = `buildBlockCsvTemplate`)。
 * 表示言語が英語のときはヘッダー行も記入例の区分値も英語にする (spec §2「英語表示対応」)。
 * `buildSpotCsvTemplate` はヘッダー行 (と説明行) だけを組み立てる下位の部品。
 *
 * 不変条件「雛形は自画面の DropZone 厳格チェックを通る」を保つため、
 * `expectedHeader` / `headerRowIndex` は呼び出し元 (`SpotCsvImport/index.tsx`)
 * が DropZone へ渡すのと同じ値をそのまま受け取る (D4)。列定義の解決
 * (`kind` / `groupType` → 通常ブロック 56列 / リンクブロック・ループ 58列 の分岐) はこの
 * builder の関心事にしない — page が既に 1 箇所で持っている解決結果
 * (`expectedHeader`) を再解決すると、同じ分岐が `SpotCsvImport/index.tsx`
 * (DropZone 用) とここ (雛形用) の 2 箇所に分裂し、ヘッダー体系が変わった時に
 * 片方だけ追随し忘れる隙ができる。
 *
 * ブロック入札も spec `spot-bid-block-csv-import.md` §1-2 の列定義どおり（日本語列名・
 * 1 行ヘッダー・56/58 列、#5705・AC-DSB-013）から生成する。旧 eSLJ 互換（英語列名・
 * 2 行ヘッダー・52/54 列）は撤回済みで、`headerRowIndex` は通常入札・
 * ブロック入札とも呼び出し元から 0 が渡る。
 */
import {
  BID_CSV_AREA_VALUE,
  BLOCK_BID_CSV_DATA_TYPE_NAME,
  BLOCK_BID_CSV_HEADER,
  BLOCK_BID_CSV_ISSUER_TYPE_VALUE,
  BLOCK_BID_CSV_SIDE_VALUE,
  type BidCsvLocalizedValue,
  type BlockBidCsvKind,
  SPOT_BID_CSV_ORDER_TYPE_VALUE,
  SPOT_BID_NORMAL_CSV_DATA_TYPE_NAME,
  SPOT_BID_NORMAL_CSV_HEADER,
  SPOT_BID_NORMAL_CSV_HEADER_BY_LANG,
  bidCsvLangOf,
  blockCsvLayoutOf,
} from '~/models/bidCsvHeader'

/**
 * カラム名行より前の行 (`headerRowIndex` 個) を埋める説明行。backend
 * (`block_bid_csv_parse.go`) はカラム名行より前を「内容は検証せず読み飛ばす」
 * ため業務上の意味を持たない (D7)。kind (通常 / ブロック) を文言に入れない
 * のは、この builder が kind を知らず「カラム名行の位置」だけで組み立てる
 * ため (下記)。
 */
const TEMPLATE_DESCRIPTION_ROW =
  'この行は説明行です (取込時に内容は確認されません)'

/**
 * スポット入札 CSV の雛形本文を組み立てる。`headerRowIndex` は DropZone と
 * 同じ「カラム名行の 0 起算位置」で、その位置までを説明行で埋めてから
 * カラム名行を置く (通常入札・ブロック入札とも 1 行ヘッダーのため現在は
 * 常に 0 → カラム名行のみ)。kind によらず引数で受け取る形を保つのは、
 * index の意味を DropZone 側 (`records[headerRowIndex]`) と一致させ、
 * 想定外の index が来ても「DL した雛形が自画面の DropZone を通る」(D4) を
 * 保つため。
 *
 * 改行は `\n` (末尾に 1 個)。RFC 4180 の `\r\n` に揃えないのは、この CSV が
 * Excel 等の表計算ソフトへの入出力ではなく DropZone の読み取り (`readCsvRecords`。LF/CRLF
 * どちらも読める) でしか消費されないため — `\r\n` は入稿済みデータのエクスポート
 * 系 (`~/models/demandGroupCsvHeader` の `serializeDemandGroupCsvRows` 等) が
 * 表計算ソフト互換を意図して採用している規約で、雛形 DL には要らない。
 * `~/pages/SpotBidding/models/blockBidCsv.ts` の CSV 出力
 * (同じく DropZone 消費用) と同じ規約に揃えている。
 */
export function buildSpotCsvTemplate(
  expectedHeader: string[],
  headerRowIndex: number,
): string {
  const descriptionRows = Array.from(
    { length: headerRowIndex },
    () => TEMPLATE_DESCRIPTION_ROW,
  )
  return `${[...descriptionRows, expectedHeader.join(',')].join('\n')}\n`
}

/**
 * 通常入札 CSV 雛形の記入例行 (spec `spot-bid-normal-csv-import.md` §2
 * 「雛形の記入例データ」の 3 行: 指値 / 成行 / 排出係数紐付け)。 受渡日はダミー値
 * (雛形を配布した時点で入札締切を過ぎた日付のまま取り込むと AC-SPOT-013 でエラーになる)。
 *
 * row の key は日本語の列名で、 書かない列は空欄。 表示言語で変わる値 (区分値・エリア・
 * ダミーの文) は日本語と英語の組で持つ。 英語のダミー値は spec「付録: 表示文言（英語）」。
 */
type NormalExampleRow = Record<string, string | BidCsvLocalizedValue>

const NORMAL_EXAMPLE_ROWS: NormalExampleRow[] = [
  {
    受渡日: '2026/09/01',
    コマ: '17',
    発注主体区分: BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail,
    エリア: BID_CSV_AREA_VALUE.tokyo,
    売買: BLOCK_BID_CSV_SIDE_VALUE.buy,
    価格種別: SPOT_BID_CSV_ORDER_TYPE_VALUE.limit,
    '価格 (円/kWh)': '10.55',
    '数量 (MW)': '10.5',
    JEPX自由記述欄: { ja: '東京需要分', en: 'For Tokyo demand' },
    メモ: { ja: '需要上振れ分を補填', en: 'Cover demand above forecast' },
  },
  {
    // 成行は価格を空欄にする (spec §8)。
    受渡日: '2026/09/01',
    コマ: '18',
    発注主体区分: BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail,
    エリア: BID_CSV_AREA_VALUE.tokyo,
    売買: BLOCK_BID_CSV_SIDE_VALUE.sell,
    価格種別: SPOT_BID_CSV_ORDER_TYPE_VALUE.market,
    '数量 (MW)': '5.0',
  },
  {
    // 排出係数紐付けが 1 の行は、 JEPX自由記述欄に判別コメント用の英字 1 文字を書く (spec §9)。
    受渡日: '2026/09/01',
    コマ: '19',
    発注主体区分: BLOCK_BID_CSV_ISSUER_TYPE_VALUE.generation,
    エリア: BID_CSV_AREA_VALUE.kansai,
    売買: BLOCK_BID_CSV_SIDE_VALUE.sell,
    価格種別: SPOT_BID_CSV_ORDER_TYPE_VALUE.limit,
    '価格 (円/kWh)': '9.80',
    '数量 (MW)': '20.0',
    排出係数紐付け: '1',
    JEPX自由記述欄: 'A',
    メモ: {
      ja: '特定契約の水力分',
      en: 'Hydro under a specific contract',
    },
  },
]

/**
 * 通常入札の雛形本文 (ヘッダー行 + 記入例 3 行)。 ヘッダーは DropZone の検証と同じ
 * 定数 (`SPOT_BID_NORMAL_CSV_HEADER` / `_EN`) から引くので、 雛形は自画面の
 * DropZone の厳格チェックを通る (D4 の不変条件)。 英語の区分値も取込で受理される
 * (AC-SPOT-001、 `conventions.md`「CSV の英語表示」)。
 */
export function buildSpotNormalCsvTemplate(isEnglish: boolean): string {
  const lang = bidCsvLangOf(isEnglish)
  const lines = [
    SPOT_BID_NORMAL_CSV_HEADER_BY_LANG[lang].join(','),
    ...NORMAL_EXAMPLE_ROWS.map((row) =>
      SPOT_BID_NORMAL_CSV_HEADER.map((column) => {
        const cell = row[column] ?? ''
        return typeof cell === 'string' ? cell : cell[lang]
      }).join(','),
    ),
  ]
  return `${lines.join('\n')}\n`
}

/** 通常入札の雛形のファイル名 (common.md §6 のデータ種別名。 表示言語で切り替える)。 */
export function spotNormalCsvTemplateFileName(isEnglish: boolean): string {
  return isEnglish
    ? `${SPOT_BID_NORMAL_CSV_DATA_TYPE_NAME.en}_template.csv`
    : `${SPOT_BID_NORMAL_CSV_DATA_TYPE_NAME.ja}_テンプレート.csv`
}

/**
 * ブロック入札 CSV 雛形の記入例行 (spec `spot-bid-block-csv-import.md` §2
 * 「雛形の記入例データ」)。 受渡日はダミー値 (雛形を配布した時点で入札締切を
 * 過ぎた日付のまま取り込むと AC-DSB-026 でエラーになる)。
 *
 * 列順はヘッダーに従い、 列名 → 値の row から組み立てる (セルの並びを手書きしない)。
 * row の key は日本語の列名。 コマ列は「コマN」列名に値を入れた分だけ埋め、
 * 書かないコマは空欄。
 *
 * 表示言語で変わる値 (区分値・エリア・ダミーの文) は日本語と英語の組で持つ
 * (spec §2「英語表示対応」)。 受渡日・ペア番号・ブロック種別・価格・コマの数量は
 * 言語で変わらないので文字列のまま。
 */
type BlockExampleRow = Record<string, string | BidCsvLocalizedValue>

/** コマ `from` から並べた値を `コマN` 列名の row に展開する。 */
function slotCells(from: number, values: number[]): BlockExampleRow {
  return Object.fromEntries(
    values.map((v, i) => [`コマ${String(from + i)}`, String(v)]),
  )
}

// エリアの英語は `conventions.md`「エリア一覧と表示順序」の英語表示。
const AREA_TOKYO: BidCsvLocalizedValue = BID_CSV_AREA_VALUE.tokyo
const AREA_KANSAI: BidCsvLocalizedValue = BID_CSV_AREA_VALUE.kansai

// JEPX自由記述欄・メモのダミー値。 英語は spec「付録: 表示文言（英語）」の
// 「雛形・記入例のダミー値」の行。
const MEMO_SURPLUS_THERMAL: BidCsvLocalizedValue = {
  ja: '火力の余剰分',
  en: 'Surplus thermal power',
}
const FREE_TEXT_MORNING_THERMAL: BidCsvLocalizedValue = {
  ja: '朝の火力増分',
  en: 'Additional thermal output in the morning',
}
const MEMO_MORNING_SPOT_SALE: BidCsvLocalizedValue = {
  ja: '朝間スポット売り',
  en: 'Morning day-ahead sale',
}
const FREE_TEXT_ARBITRAGE: BidCsvLocalizedValue = {
  ja: '東京・関西の裁定',
  en: 'Tokyo-Kansai arbitrage',
}
const MEMO_PRICE_SPREAD: BidCsvLocalizedValue = {
  ja: '東西の値差狙い',
  en: 'Targeting the east-west price spread',
}

const STANDARD_EXAMPLE_ROWS: BlockExampleRow[] = [
  {
    受渡日: '2026/09/01',
    発注主体区分: BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail,
    エリア: AREA_TOKYO,
    売買: BLOCK_BID_CSV_SIDE_VALUE.sell,
    '価格 (円/kWh)': '10.00',
    メモ: MEMO_SURPLUS_THERMAL,
    // コマ6 の 0 は、 ブロックの途中で入札しないコマの例。
    ...slotCells(1, [100, 100, 100, 80, 80, 0, 100, 100]),
  },
]

const LINK_EXAMPLE_ROWS: BlockExampleRow[] = [
  {
    受渡日: '2026/09/01',
    ペア番号: '1',
    ブロック種別: 'P',
    発注主体区分: BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail,
    エリア: AREA_TOKYO,
    売買: BLOCK_BID_CSV_SIDE_VALUE.sell,
    '価格 (円/kWh)': '10.00',
    JEPX自由記述欄: FREE_TEXT_MORNING_THERMAL,
    メモ: MEMO_MORNING_SPOT_SALE,
    ...slotCells(1, [100, 100, 100, 100, 100, 100, 100, 100]),
  },
  {
    受渡日: '2026/09/01',
    ペア番号: '1',
    ブロック種別: 'C',
    発注主体区分: BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail,
    エリア: AREA_TOKYO,
    売買: BLOCK_BID_CSV_SIDE_VALUE.sell,
    '価格 (円/kWh)': '11.00',
    JEPX自由記述欄: FREE_TEXT_MORNING_THERMAL,
    メモ: MEMO_MORNING_SPOT_SALE,
    ...slotCells(9, [100, 100, 100, 100, 100, 100, 100, 100]),
  },
]

const LOOP_EXAMPLE_ROWS: BlockExampleRow[] = [
  {
    受渡日: '2026/09/01',
    ペア番号: '1',
    ブロック種別: 'A',
    発注主体区分: BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail,
    エリア: AREA_TOKYO,
    売買: BLOCK_BID_CSV_SIDE_VALUE.sell,
    '価格 (円/kWh)': '10.00',
    JEPX自由記述欄: FREE_TEXT_ARBITRAGE,
    メモ: MEMO_PRICE_SPREAD,
    ...slotCells(1, [100, 100, 100, 100, 100, 100, 100, 100]),
  },
  {
    受渡日: '2026/09/01',
    ペア番号: '1',
    ブロック種別: 'B',
    発注主体区分: BLOCK_BID_CSV_ISSUER_TYPE_VALUE.retail,
    エリア: AREA_KANSAI,
    売買: BLOCK_BID_CSV_SIDE_VALUE.buy,
    '価格 (円/kWh)': '9.50',
    JEPX自由記述欄: FREE_TEXT_ARBITRAGE,
    メモ: MEMO_PRICE_SPREAD,
    ...slotCells(1, [100, 100, 100, 100, 100, 100, 100, 100]),
  },
]

const EXAMPLE_ROWS: Record<BlockBidCsvKind, BlockExampleRow[]> = {
  standard: STANDARD_EXAMPLE_ROWS,
  link: LINK_EXAMPLE_ROWS,
  loop: LOOP_EXAMPLE_ROWS,
}

/**
 * ブロック入札の雛形本文 (ヘッダー行 + 記入例行)。 雛形は種別ごとに 3 つで、
 * それぞれ spec §2 の該当種別の記入例だけを入れる (1 ファイルに LINK と LOOP を
 * 混ぜると AC-DSB-012 で取り込めないため。 spec §6)。 表示言語が英語のときは
 * ヘッダー行も記入例の区分値も英語で出す (spec §2「英語表示対応」)。
 *
 * ヘッダーは DropZone の検証と同じ表 (`BLOCK_BID_CSV_HEADER`) から引く。 雛形と検証が
 * 同じ表を見るので、 雛形は自画面の DropZone の厳格チェックを通る (D4 の不変条件)。
 */
export function buildBlockCsvTemplate(
  type: BlockBidCsvKind,
  isEnglish: boolean,
): string {
  const layout = blockCsvLayoutOf(type)
  const lang = bidCsvLangOf(isEnglish)
  // 記入例 row の key は日本語の列名なので、 セルは日本語ヘッダーの列順で引く
  // (英語ヘッダーも同じ列順)。
  const columns = BLOCK_BID_CSV_HEADER.ja[layout]
  const lines = [
    BLOCK_BID_CSV_HEADER[lang][layout].join(','),
    ...EXAMPLE_ROWS[type].map((row) =>
      columns
        .map((column) => {
          const cell = row[column] ?? ''
          return typeof cell === 'string' ? cell : cell[lang]
        })
        .join(','),
    ),
  ]
  return `${lines.join('\n')}\n`
}

/**
 * ブロック入札の雛形のファイル名 (common.md §6 の雛形ごとのデータ種別名、 #9332)。
 * 表示言語で切り替え、 列数は入れない。 データ種別名は入札一覧の CSV ダウンロードと共通
 * (`BLOCK_BID_CSV_DATA_TYPE_NAME`)。
 */
export function blockBidCsvTemplateFileName(
  type: BlockBidCsvKind,
  isEnglish: boolean,
): string {
  const name = BLOCK_BID_CSV_DATA_TYPE_NAME[type]
  return isEnglish ? `${name.en}_template.csv` : `${name.ja}_テンプレート.csv`
}
